import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Tooltip,
  Space,
  Spin
} from "antd";
import { QuestionCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/plots";
import moment from "moment";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import ViewTypeSelector from "../AdminThroughputCharts/ViewTypeSelector";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";

const { Title } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const FreeTrialsChart = ({ chartStyle, dateRange, onDateChange }) => {
  const [viewType, setViewType] = useState("Daily");
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch free trial signups data
  const fetchFreeTrialData = async () => {
    if (!dateRange?.start || !dateRange?.end) return;
    
    try {
      setIsLoading(true);
      const payload = {
        action: "get_free_trial_ai_signup_count",
        start_date: moment(dateRange.start).format("YYYY-MM-DD"),
        end_date: moment(dateRange.end).format("YYYY-MM-DD")
      };

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      const data = response.data || [];
      
      // Transform API data to chart format
      const transformedData = data.map(item => ({
        date: moment(item.date, 'YYYY-MM-DD').format('YYYY/MM/DD'),
        signups: parseInt(item.count, 10),
        type: 'Free Trial Signups'
      }));

      setChartData(transformedData);
    } catch (error) {
      console.error("Error fetching free trial data:", error);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when date range changes
  useEffect(() => {
    fetchFreeTrialData();
  }, [dateRange]);

  // Process data based on view type
  const processedData = useMemo(() => {
    if (viewType === "Daily") {
      return chartData;
    }

    if (viewType === "Weekly") {
      const weeklyData = new Map();
      
      chartData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const weekStart = itemDate.clone().startOf('week');
        const weekEnd = itemDate.clone().endOf('week');
        const weekLabel = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')}`;
        const weekKey = weekStart.format('YYYY/MM/DD');
        
        if (!weeklyData.has(weekKey)) {
          weeklyData.set(weekKey, {
            date: weekLabel,
            signups: 0,
            type: 'Free Trial Signups',
            sortDate: weekStart.format('YYYY/MM/DD')
          });
        }
        weeklyData.get(weekKey).signups += item.signups;
      });
      
      return Array.from(weeklyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    if (viewType === "Monthly") {
      const monthlyData = new Map();
      
      chartData.forEach((item) => {
        const itemDate = moment(item.date, 'YYYY/MM/DD');
        const monthStart = itemDate.clone().startOf('month');
        const monthLabel = monthStart.format('MMM YYYY');
        const monthKey = monthStart.format('YYYY/MM');
        
        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, {
            date: monthLabel,
            signups: 0,
            type: 'Free Trial Signups',
            sortDate: monthStart.format('YYYY/MM/DD')
          });
        }
        monthlyData.get(monthKey).signups += item.signups;
      });
      
      return Array.from(monthlyData.values()).sort((a, b) => 
        moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
      );
    }

    return chartData;
  }, [chartData, viewType]);

  const chartConfig = useMemo(() => {
    return {
      data: processedData,
      xField: 'date',
      yField: 'signups',
      smooth: true,
      animation: false,
      color: '#1890ff',
      point: {
        size: 4,
        shape: 'circle',
        style: {
          fill: '#1890ff',
          stroke: '#fff',
          lineWidth: 2,
        },
      },
      lineStyle: {
        lineWidth: 2,
      },
      xAxis: {
        label: {
          autoRotate: true,
          autoHide: true,
        },
      },
      yAxis: {
        label: {
          formatter: (v) => `${v}`,
        },
        title: {
          text: 'Signups',
        },
      },
      tooltip: {
        formatter: (datum) => {
          return {
            name: 'Signups',
            value: datum.signups,
          };
        },
      },
    };
  }, [processedData]);

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>
            Free Trial Signups{' '}
            <Tooltip title="Daily, weekly, or monthly count of new free trial signups">
              <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Title>
          <Row gutter={12}>
            <Col>
              <Space>
                <DateRangePicker
                  start={dateRange.start}
                  end={dateRange.end}
                  date_format={DATE_FORMAT}
                  onCalChange={onDateChange}
                  disabledDate={(current) => current && current.isBefore(moment('2026-01-01'), 'day')}
                />
                <ViewTypeSelector
                  value={viewType}
                  onChange={setViewType}
                />
              </Space>
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Spin spinning={isLoading} indicator={antIcon}>
          <Line {...chartConfig} />
        </Spin>
      </Card>
    </Col>
  );
};

export default FreeTrialsChart;
